import React from 'react';
import SmoothCollapse from 'react-smooth-collapse';
import {
  Property,
  MobilePropertyValue,
  Row,
  MobileContainer,
  CollapseRow,
  PropertyName,
  CaretButton,
  StyledCaret,
  PropertySubtitle,
} from '../styles';
import { formatCurrency } from '../../../helpers/formatNumber';
import { DesktopAndTablet, Mobile } from '../../../helpers/responsive';
import toolTipContent from '../../../helpers/toolTipContent';
import { useThemeContext } from '../../../elements/theme/CustomThemeProvider';
import { POLICY_OPTIONS, PROPERTY_LABELS } from '../../../constants';
import styled from 'styled-components';
import lightbulbIcon from '../../../icons/lightbulb.svg';

interface PolicyItemProps {
  propertyName: string;
  propertyValue?: number;
  isReadOnly?: boolean;
  toolTipContentKey?: string;
  expanded: boolean;
  setExpanded: (...args: any[]) => any;
  testId?: string;
  customColors?: Record<string, any>;
  customDefaultBg?: string;
  children: React.ReactNode;
  disableSubmit?: boolean;
  labelSubtitle?: string;
  isFlorida?: boolean;
  policyType?: string;
  description?: React.ReactNode;
  coastalMessage?: string;
}

const PolicyItem = ({
  propertyName,
  propertyValue,
  isReadOnly = false,
  toolTipContentKey = '',
  children,
  expanded,
  setExpanded,
  testId,
  customColors,
  customDefaultBg,
  disableSubmit = false,
  labelSubtitle,
  isFlorida = false,
  policyType,
  description,
  coastalMessage,
}: PolicyItemProps) => {
  const { theme } = useThemeContext();
  const showToolTip = Boolean(toolTipContentKey);
  const isExpandable = !isReadOnly || showToolTip || Boolean(description) || Boolean(coastalMessage);
  const showPropertyValue = typeof propertyValue === 'number' || typeof propertyValue === 'string';

  const defaultBg = customDefaultBg ?? '';

  const renderPropertyValue = (value?: number | string) => {
    if (typeof value === 'string') {
      return value;
    }
    return formatCurrency(value);
  };

  const showLabelSubtitle = isFlorida && labelSubtitle;

  const label = (propertyName: string) => {
    if (isFlorida && policyType === POLICY_OPTIONS.PROPERTY_CLAIMS_DEDUCTIBLE) {
      return PROPERTY_LABELS['PROPERTY_CLAIMS_DEDUCTIBLE_FL'];
    }
    return propertyName;
  };

  return (
    <div className="PolicyItem">
      <Row
        onClick={() => isExpandable && setExpanded(expanded ? '' : propertyName)}
        data-cy={`summary-${propertyName.replace(/ /g, '_')}-expand`}
        data-testid={testId || propertyName}
        borderColor={customColors?.borderColor}
        backgroundColor={expanded ? customColors?.backgroundColor : defaultBg}
      >
        <DesktopAndTablet>
          <Property isReadOnly={!isExpandable}>
            <PropertyName>
              {label(propertyName)}
              {showLabelSubtitle && <PropertySubtitle> - {labelSubtitle}</PropertySubtitle>}
            </PropertyName>
          </Property>
          <Property isReadOnly={!isExpandable}>
            {showPropertyValue && <PropertyValue>{renderPropertyValue(propertyValue)}</PropertyValue>}
            {isExpandable && (
              <CaretButton disabled={disableSubmit}>
                <StyledCaret src={theme.icons.caret} expanded={expanded} />
              </CaretButton>
            )}
          </Property>
        </DesktopAndTablet>
        <Mobile>
          <MobileContainer>
            <Row>
              <Property isReadOnly={!isExpandable}>
                {label(propertyName)} {showLabelSubtitle && `- ${labelSubtitle}`}
              </Property>
              {isExpandable && (
                <CaretButton disabled={disableSubmit}>
                  <StyledCaret src={theme.icons.caret} expanded={expanded} />
                </CaretButton>
              )}
            </Row>
            <Row>
              {!expanded && showPropertyValue && (
                <MobilePropertyValue>{renderPropertyValue(propertyValue)}</MobilePropertyValue>
              )}
            </Row>
          </MobileContainer>
        </Mobile>
      </Row>
      <SmoothCollapse expanded={expanded} allowOverflowWhenOpen>
        <CollapseRow disableDivStyles={policyType === POLICY_OPTIONS.RETROACTIVE_DATE}>
          {coastalMessage && (
            <CoastalMessage>
              <img src={lightbulbIcon} alt="" width={14} height={14} />
              {coastalMessage}
            </CoastalMessage>
          )}
          <Description>{description ?? (showToolTip && toolTipContent[toolTipContentKey])}</Description>
          {children}
        </CollapseRow>
      </SmoothCollapse>
    </div>
  );
};

const PropertyValue = styled.span`
  white-space: nowrap;
`;

const Description = styled.section`
  p {
    margin-left: 0;
    margin-right: 0;
  }
`;

const CoastalMessage = styled.p`
  display: flex;
  align-items: center;
  align-self: stretch;
  box-sizing: border-box;
  gap: 8px;
  margin: 10px 0;
  padding: 8px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.neutral.special};
  font-family: ${({ theme }) => theme.font.typeface.secondary};
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.tertiary.zero};
`;

export default PolicyItem;
